import axios, { AxiosError } from 'axios'
import { EventBus } from '@/plugins/eventBus'
import { formatLocalizedDateLong, formatLocalizedDateTime } from '@/utils/dateTimePicker2Date'

export interface ChangelogFetchResult<T> {
  items: T[]
  error: string | null
}

export function parseChangelogDate(value: string): Date | null {
  if (!value) return null
  const date = new Date(value.replace(' ', 'T'))
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatChangelogDateTime(value: string, locale: string): string {
  return formatLocalizedDateTime(value, locale)
}

export function formatChangelogDateLong(value: string, locale: string): string {
  return formatLocalizedDateLong(value, locale)
}

export function resolveChangelogAxiosErrorMessage(
  err: AxiosError,
  translate: (key: string) => unknown,
): string {
  const responseData = err.response?.data
  if (typeof responseData === 'string' && responseData.trim()) {
    const key = `$vuetify.api_errors.${responseData}`
    const translated = translate(key)
    if (translated !== key) return String(translated)
    return responseData
  }

  const status = err.response?.status
  if (status) {
    const key = `$vuetify.api_errors.${status}`
    const translated = translate(key)
    if (translated !== key) return String(translated)
  }

  return String(translate('$vuetify.api_errors.network'))
}

export async function fetchChangelogEntries<T>(
  url: string,
  params: Record<string, string | number> = {},
  options: {
    resolveError?: (err: AxiosError) => string
  } = {},
): Promise<ChangelogFetchResult<T>> {
  try {
    const res = await axios.get<T[]>(url, { params })
    return { items: res.data, error: null }
  } catch (err) {
    const axiosError = err as AxiosError
    EventBus.$emit('snackbar', { axiosError })
    const error = options.resolveError
      ? options.resolveError(axiosError)
      : axiosError.message
    return { items: [], error }
  }
}
